import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { SortableHeader } from "../components/shared/SortableHeader";
import { Wrench, Plus, Edit, Trash2, QrCode, Link2, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { EmptyState } from "../components/shared/EmptyState";
import { QRCodeModal } from "../components/shared/QRCodeModal";
import { SearchFilterBar } from "../components/shared/SearchFilterBar";
import { MachineBlueprintLinker } from "../components/shared/MachineBlueprintLinker";
import { ExportButton } from "../components/shared/ExportButton";

const INITIAL_FORM = { machine_number: "", serial_number: "", asset_number: "", name: "", type: "", category: "", location: "", description: "" };

function MachineCard({ machine, onEdit, onDelete, onGenerateQR, onLinkBlueprints, canEdit, canDelete, catColorMap }) {
  const catStyle = machine.category && catColorMap[machine.category]
    ? { backgroundColor: catColorMap[machine.category].bg, color: catColorMap[machine.category].color }
    : { backgroundColor: "#F1F5F9", color: "#64748B" };

  return (
    <div className="bg-white border border-[#E2E8F0] p-6 hover:border-[#0F172A] transition-colors duration-150" data-testid={`machine-card-${machine.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="font-mono text-xl font-bold text-[#2563EB]">{machine.machine_number}</div>
            {machine.category && (
              <span className="px-2 py-0.5 text-xs font-bold tracking-wider" style={catStyle} data-testid={`machine-category-badge-${machine.id}`}>
                {machine.category}
              </span>
            )}
          </div>
          <h3 className="font-heading font-bold text-lg text-[#0F172A] mt-1">{machine.name}</h3>
        </div>
        <Wrench size={20} className="text-[#64748B]" />
      </div>
      <div className="space-y-2 text-sm mb-4">
        <div><span className="text-[#64748B]">Type:</span><span className="ml-2 text-[#0F172A] font-medium">{machine.type}</span></div>
        {machine.serial_number && <div><span className="text-[#64748B]">Serial #:</span><span className="ml-2 text-[#0F172A] font-mono text-xs">{machine.serial_number}</span></div>}
        {machine.asset_number && <div><span className="text-[#64748B]">Asset #:</span><span className="ml-2 text-[#0F172A] font-mono text-xs">{machine.asset_number}</span></div>}
        {machine.location && <div><span className="text-[#64748B]">Location:</span><span className="ml-2 text-[#0F172A]">{machine.location}</span></div>}
        {machine.description && <div className="text-[#64748B] text-xs mt-2">{machine.description}</div>}
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => onGenerateQR(machine)} className="flex-1 rounded-none border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white" data-testid={`qr-machine-${machine.id}`}>
          <QrCode size={14} className="mr-1" />QR
        </Button>
        {canEdit && (
          <Button size="sm" variant="outline" onClick={() => onLinkBlueprints(machine)} className="flex-1 rounded-none border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white" data-testid={`link-machine-${machine.id}`}>
            <Link2 size={14} className="mr-1" />Prints
          </Button>
        )}
        {canEdit && (
          <Button size="sm" variant="outline" onClick={() => onEdit(machine)} className="flex-1 rounded-none border-[#E2E8F0]" data-testid={`edit-machine-${machine.id}`}>
            <Edit size={14} className="mr-1" />Edit
          </Button>
        )}
        {canDelete && (
          <Button size="sm" variant="outline" onClick={() => onDelete(machine.id)} className="flex-1 rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white" data-testid={`delete-machine-${machine.id}`}>
            <Trash2 size={14} className="mr-1" />Delete
          </Button>
        )}
      </div>
    </div>
  );
}

function MachineFormDialog({ open, onOpenChange, editingMachine, formData, setFormData, onSubmit, onReset, defaultCategory, categoryOptions, typeOptions }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) onReset(); }}>
      <DialogTrigger asChild>
        <Button className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none transition-colors duration-150 tracking-wide" data-testid="add-machine-button">
          <Plus size={16} className="mr-2" />ADD MACHINE
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white rounded-none border border-[#E2E8F0]" aria-describedby="machine-form-description">
        <DialogHeader><DialogTitle className="font-heading font-bold text-[#0F172A]">{editingMachine ? "Edit Machine" : "Add Machine"}</DialogTitle></DialogHeader>
        <div id="machine-form-description" className="sr-only">Form to create or edit machine records</div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label className="text-[#0F172A]">Category *</Label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              required
              className="mt-1 w-full h-10 px-3 rounded-none border border-[#E2E8F0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
              data-testid="machine-category-select"
            >
              <option value="">Select Category</option>
              {categoryOptions.map((c) => <option key={c.name} value={c.name}>{c.label || c.name}</option>)}
            </select>
          </div>
          <div><Label className="text-[#0F172A]">Machine Number *</Label><Input value={formData.machine_number} onChange={(e) => setFormData({ ...formData, machine_number: e.target.value })} required className="mt-1 rounded-none border-[#E2E8F0] font-mono" placeholder={formData.category ? `${formData.category}-123` : "M-123"} data-testid="machine-number-input" /></div>
          <div><Label className="text-[#0F172A]">Serial Number</Label><Input value={formData.serial_number} onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })} className="mt-1 rounded-none border-[#E2E8F0] font-mono" placeholder="SN123456789" data-testid="serial-number-input" /><p className="text-xs text-[#64748B] mt-1">Manufacturer's serial number</p></div>
          <div><Label className="text-[#0F172A]">Asset Number</Label><Input value={formData.asset_number} onChange={(e) => setFormData({ ...formData, asset_number: e.target.value })} className="mt-1 rounded-none border-[#E2E8F0] font-mono" placeholder="ASSET-001" data-testid="asset-number-input" /><p className="text-xs text-[#64748B] mt-1">Internal asset tracking number</p></div>
          <div><Label className="text-[#0F172A]">Name *</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="mt-1 rounded-none border-[#E2E8F0]" data-testid="machine-name-input" /></div>
          <div>
            <Label className="text-[#0F172A]">Type *</Label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              required
              className="mt-1 w-full h-10 px-3 rounded-none border border-[#E2E8F0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
              data-testid="machine-type-select"
            >
              <option value="">Select Type</option>
              {typeOptions.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
            </select>
          </div>
          <div><Label className="text-[#0F172A]">Location</Label><Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="mt-1 rounded-none border-[#E2E8F0]" data-testid="machine-location-input" /></div>
          <div><Label className="text-[#0F172A]">Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="mt-1 rounded-none border-[#E2E8F0]" rows={3} data-testid="machine-description-input" /></div>
          <Button type="submit" className="w-full bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="submit-machine-button">{editingMachine ? "UPDATE" : "CREATE"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function MachinesPage({ onLogout, userRole, isFeatureEnabled, category, machineCategories = [] }) {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [formData, setFormData] = useState({ ...INITIAL_FORM, category: category || "" });
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [linkerMachine, setLinkerMachine] = useState(null);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [typeOptions, setTypeOptions] = useState([]);

  const canEdit = userRole !== "hourly";
  const canDelete = userRole === "admin" || userRole === "superintendent";

  // Derive title from category data
  const catObj = machineCategories.find((c) => c.name === category);
  const pageTitle = catObj ? `${catObj.label}` : category ? `${category} Machine Records` : "Machine Records";
  const pageSubtitle = catObj ? `${catObj.label} — machines and equipment` : "Track and manage industrial equipment";

  // Build color map for category badges
  const catColorMap = {};
  machineCategories.forEach((c) => { catColorMap[c.name] = { color: c.color, bg: c.bg_color }; });

  // Fetch machine types
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const resp = await axios.get(`${API}/admin/machine-types`, { headers: getAuthHeaders() });
        setTypeOptions(resp.data || []);
      } catch { /* fallback to empty */ }
    };
    fetchTypes();
  }, []);

  const fetchMachines = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (typeFilter) params.set("machine_type", typeFilter);
      if (locationFilter) params.set("location", locationFilter);
      if (sortBy) params.set("sort_by", sortBy);
      if (sortOrder) params.set("sort_order", sortOrder);
      if (category) params.set("category", category);
      const response = await axios.get(`${API}/machines?${params}`, { headers: getAuthHeaders() });
      setMachines(response.data);
    } catch { toast.error("Failed to load machines"); }
    finally { setLoading(false); }
  }, [search, typeFilter, locationFilter, sortBy, sortOrder, category]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchMachines(), 300);
    return () => clearTimeout(debounce);
  }, [fetchMachines]);

  const resetForm = () => { setFormData({ ...INITIAL_FORM, category: category || "" }); setEditingMachine(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMachine) {
        await axios.put(`${API}/machines/${editingMachine.id}`, formData, { headers: getAuthHeaders() });
        toast.success("Machine updated");
      } else {
        await axios.post(`${API}/machines`, formData, { headers: getAuthHeaders() });
        toast.success("Machine created");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchMachines();
    } catch { toast.error(editingMachine ? "Update failed" : "Creation failed"); }
  };

  const handleEdit = (machine) => {
    setEditingMachine(machine);
    setFormData({ machine_number: machine.machine_number, serial_number: machine.serial_number || "", asset_number: machine.asset_number || "", name: machine.name, type: machine.type, category: machine.category || category || "", location: machine.location || "", description: machine.description || "" });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this machine?")) return;
    try {
      await axios.delete(`${API}/machines/${id}`, { headers: getAuthHeaders() });
      toast.success("Machine deleted");
      fetchMachines();
    } catch { toast.error("Delete failed"); }
  };

  const handleGenerateQR = async (machine) => {
    try {
      const response = await axios.get(`${API}/machines/${machine.id}/qrcode`, { headers: getAuthHeaders() });
      setQrCodeData({ ...response.data, machine });
      setShowQrModal(true);
    } catch { toast.error("Failed to generate QR code"); }
  };

  const clearFilters = () => { setSearch(""); setTypeFilter(""); setLocationFilter(""); };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  // Get unique types and locations from current data for filter dropdowns
  const uniqueTypes = [...new Set(machines.map((m) => m.type).filter(Boolean))];
  const uniqueLocations = [...new Set(machines.map((m) => m.location).filter(Boolean))];

  if (loading) {
    return <Layout onLogout={onLogout} userRole={userRole}><div className="flex items-center justify-center h-64"><div className="text-[#64748B] font-mono">Loading {category ? `${category} machines` : "machines"}...</div></div></Layout>;
  }

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-4" data-testid="machines-page">
        <PageHeader title={pageTitle} subtitle={pageSubtitle} testId="machines-title">
          <div className="flex gap-2">
            <ExportButton endpoint={`/export/machines${category ? `?category=${category}` : ""}`} filename={`${category ? category.toLowerCase() + "_" : ""}machines_export.csv`} />
            {canEdit && <MachineFormDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} editingMachine={editingMachine} formData={formData} setFormData={setFormData} onSubmit={handleSubmit} onReset={resetForm} defaultCategory={category} categoryOptions={machineCategories} typeOptions={typeOptions} />}
          </div>
        </PageHeader>

        <SearchFilterBar
          search={search}
          onSearchChange={setSearch}
          onClear={clearFilters}
          filters={[
            { key: "type", placeholder: "Types", value: typeFilter, onChange: setTypeFilter, options: uniqueTypes.map((t) => ({ value: t, label: t })) },
            { key: "location", placeholder: "Locations", value: locationFilter, onChange: setLocationFilter, options: uniqueLocations.map((l) => ({ value: l, label: l })) },
          ]}
        />

        {machines.length === 0 ? (
          <EmptyState icon={Wrench} message="No machines found. Add your first machine record." />
        ) : (
          <>
            <div className="flex items-center gap-2 text-xs text-[#64748B]" data-testid="machines-sort">
              <ArrowUpDown size={14} />
              <span>Sort:</span>
              {[{label:"Name",field:"name"},{label:"Machine #",field:"machine_number"},{label:"Type",field:"type"},{label:"Location",field:"location"},{label:"Date",field:"created_at"}].map(({label,field}) => (
                <button key={field} onClick={() => handleSort(field)} className={`px-2 py-0.5 rounded text-xs transition-colors ${sortBy === field ? "bg-[#0F172A] text-white" : "bg-[#F1F5F9] hover:bg-[#E2E8F0]"}`} data-testid={`sort-btn-${field}`}>
                  {label} {sortBy === field ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {machines.map((m) => <MachineCard key={m.id} machine={m} onEdit={handleEdit} onDelete={handleDelete} onGenerateQR={handleGenerateQR} onLinkBlueprints={setLinkerMachine} canEdit={canEdit} canDelete={canDelete} catColorMap={catColorMap} />)}
            </div>
          </>
        )}
      </div>

      <QRCodeModal open={showQrModal} onClose={setShowQrModal} qrCodeData={qrCodeData} />
      <MachineBlueprintLinker open={!!linkerMachine} onClose={() => setLinkerMachine(null)} machine={linkerMachine} />
    </Layout>
  );
}
